/**
 * Pure rules for a message's call-to-action row: what counts as a row, who may
 * click it, and what a button actually does.
 *
 * Behaviour only — the shapes live in `fixtures/domain.ts`. Two callers need
 * these: the renderer asking a message for its row, and the wire mapper in
 * `stream.ts` deciding whether what it just parsed is a row at all. Stated once
 * so the mapper validates against the domain rather than a private copy of it.
 */
import { SYSTEM_SENDER_ID } from './first-reply.js';

import type {
  CtaButton,
  CtaButtonMode,
  CtaButtonStyle,
  CtaPayload,
  Message,
  ParticipantId,
} from '../fixtures/domain.js';
import { isEmployerSide } from '@chat-emp/shared';

const CTA_STYLES: readonly CtaButtonStyle[] = ['primary', 'secondary', 'danger'];
const CTA_MODES: readonly CtaButtonMode[] = ['event', 'url', 'reply', 'url_reply'];

/** Guards for the two closed vocabularies, for the mapper's defensive parse. */
export function isCtaButtonStyle(value: unknown): value is CtaButtonStyle {
  return CTA_STYLES.includes(value as CtaButtonStyle);
}

export function isCtaButtonMode(value: unknown): value is CtaButtonMode {
  return CTA_MODES.includes(value as CtaButtonMode);
}

/** An empty `buttons` array is not a row. */
export function hasButtons(cta: CtaPayload): boolean {
  return cta.buttons.length > 0;
}

/** The row, or nothing. */
export function ctaOf(message: Message): CtaPayload | null {
  const cta = message.cta;
  return cta && hasButtons(cta) ? cta : null;
}

/**
 * Whether this user may click this message's row. All four must hold:
 *
 *  1. the row is not already spent
 *  2. the user is not on the explicit exclusion list
 *  3. on a normal message, the user is not the sender
 *  4. on a system note, rule 3 does not apply
 *
 * Rule 3 is the one that surprises people, and it is not politeness: an offer
 * you can accept on the other party's behalf is not an offer. It is implicit so
 * that nobody has to remember to add themselves to `excludeUserIds` and nobody
 * can forget. Rule 4 falls out of it — a system note is written by no one, so
 * there is no author to exclude.
 */
/** Same seat: the same id, or two employer-side ids (a recruiter and the company
 *  it wrote as before the cutover). */
function sameSide(a: ParticipantId, b: ParticipantId): boolean {
  return a === b || (isEmployerSide(a) && isEmployerSide(b));
}

export function canClickCta(message: Message, userId: ParticipantId): boolean {
  const cta = ctaOf(message);
  if (!cta || cta.completed) return false;
  if (cta.excludeUserIds?.includes(userId)) return false;
  if (!message.system && sameSide(message.senderId, userId)) return false;
  return true;
}

/**
 * Whether a message may carry a CTA row at all — the forgery gate.
 *
 * `cta` is a plain custom Stream field any member may attach to their own
 * message, and `canClickCta` deliberately lets the NON-sender click — so a row
 * the jobseeker authored on a 1-on-1 chat is a button the employer is invited
 * to press, and a `reply` button there posts the jobseeker's chosen words
 * under the employer's name.
 *
 * An allowlist of authors who cannot be forged, never a blocklist of the
 * counterpart, so an unexpected sender fails closed. A Stream client user can
 * only send as themselves, which is what makes the two trusted ids
 * trustworthy; on a broadcast thread every row is the platform's campaign
 * (the persona authors them), so the author does not matter there.
 */
export function mayCarryCta({
  broadcast,
  senderId,
  sessionUserId,
}: {
  broadcast: boolean;
  senderId: ParticipantId;
  sessionUserId: ParticipantId;
}): boolean {
  return broadcast || sameSide(senderId, sessionUserId) || senderId === SYSTEM_SENDER_ID;
}

/** CTA urls answered by this BFF, sent with the employer's session. Exact paths, never a prefix:
 *  a message must not be able to aim the session at any other route. */
export const BFF_CTA_ROUTES: ReadonlySet<string> = new Set(['/api/chat-day/respond']);

/** A button carrying a `reply` is a reply button whether or not it says so, since campaign
 *  tooling omits the mode. `url_reply` carries a reply by design, so it keeps its own mode. */
export function ctaModeOf(button: CtaButton): CtaButtonMode {
  if (button.mode === 'url_reply') return 'url_reply';
  return button.reply ? 'reply' : button.mode;
}
